<script>
import { ref, computed } from 'vue';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useConversationLabels } from 'dashboard/composables/useConversationLabels';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { usePilotConfigStore } from 'dashboard/store/pilot/preferences';
import Spinner from 'shared/components/Spinner.vue';
import LabelDropdown from 'shared/components/ui/label/LabelDropdown.vue';
import AddLabel from 'shared/components/ui/dropdown/AddLabel.vue';
import LabelSuggestion from 'dashboard/components/widgets/conversation/conversation/LabelSuggestion.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

export default {
  components: {
    Spinner,
    LabelDropdown,
    AddLabel,
    LabelSuggestion,
    Icon,
  },
  setup() {
    const { isAdmin, isCloudFeatureEnabled } = useAccount();
    const pilotConfigStore = usePilotConfigStore();

    const {
      contactId,
      savedLabels,
      activeLabels,
      accountLabels,
      addLabelToConversation,
      removeLabelFromConversation,
    } = useConversationLabels();

    const showSearchDropdownLabel = ref(false);

    const toggleLabels = () => {
      showSearchDropdownLabel.value = !showSearchDropdownLabel.value;
    };

    const closeDropdownLabel = () => {
      showSearchDropdownLabel.value = false;
    };

    const keyboardEvents = {
      KeyL: {
        action: e => {
          e.preventDefault();
          toggleLabels();
        },
      },
      Escape: {
        action: () => {
          if (showSearchDropdownLabel.value) {
            toggleLabels();
          }
        },
        allowOnFocusedInput: true,
      },
    };
    useKeyboardEvents(keyboardEvents);

    const pilotTasksEnabled = computed(() =>
      isCloudFeatureEnabled(FEATURE_FLAGS.PILOT_TASKS)
    );

    const isLabelSuggestionEnabled = computed(() => {
      if (!pilotTasksEnabled.value) return false;
      return pilotConfigStore.features?.label_suggestion?.enabled === true;
    });

    const showLabelSuggestionButton = computed(() => {
      return pilotTasksEnabled.value && isLabelSuggestionEnabled.value;
    });

    return {
      isAdmin,
      contactId,
      savedLabels,
      activeLabels,
      accountLabels,
      addLabelToConversation,
      removeLabelFromConversation,
      showSearchDropdownLabel,
      closeDropdownLabel,
      toggleLabels,
      showLabelSuggestionButton,
    };
  },
  data() {
    return {
      selectedLabels: [],
    };
  },

  computed: {
    ...mapGetters({
      contactUiFlags: 'contactLabels/getUIFlags',
    }),
  },
  watch: {
    contactId: {
      immediate: true,
      handler(newContactId) {
        if (newContactId) {
          this.$store.dispatch('contactLabels/get', newContactId);
        }
      },
    },
  },
  methods: {
    async triggerLabelSuggestion() {
      const result = await this.$refs.labelSuggestion?.trigger();
      if (result?.error) {
        useAlert(result.error);
      }
    },
  },
};
</script>

<template>
  <div class="sidebar-labels-wrap">
    <div
      v-if="!contactUiFlags.isFetching"
      class="contact-conversation--list"
    >
      <div
        v-on-clickaway="closeDropdownLabel"
        class="label-wrap flex flex-wrap"
        @keyup.esc="closeDropdownLabel"
      >
        <AddLabel @add="toggleLabels" />
        <woot-label
          v-for="label in activeLabels"
          :key="label.id"
          :title="label.title"
          :description="label.description"
          show-close
          :color="label.color"
          variant="smooth"
          class="max-w-[calc(100%-0.5rem)]"
          @remove="removeLabelFromConversation"
        />

        <div
          :class="{
            'block visible': showSearchDropdownLabel,
            'hidden invisible': !showSearchDropdownLabel,
          }"
          class="border rounded-lg bg-n-alpha-3 top-6 backdrop-blur-[100px] absolute w-full shadow-lg border-n-strong dark:border-n-strong p-2 box-border z-[9999]"
        >
          <LabelDropdown
            v-if="showSearchDropdownLabel"
            :account-labels="accountLabels"
            :selected-labels="savedLabels"
            :allow-creation="isAdmin"
            @add="addLabelToConversation"
            @remove="removeLabelFromConversation"
          />
        </div>
      </div>

      <!-- AI 建议标签按钮和结果 -->
      <div v-if="showLabelSuggestionButton" class="mt-3">
        <button
          v-tooltip.top="$t('LABEL_MGMT.SUGGESTIONS.TOOLTIP.SINGLE_SUGGESTION')"
          type="button"
          class="ai-suggest-btn"
          @click="triggerLabelSuggestion"
        >
          <Icon icon="i-lucide-tags" class="size-3" />
          <span>{{
            $t('INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.LABEL_SUGGESTION')
          }}</span>
        </button>
      </div>

      <!-- AI 建议标签组件 -->
      <LabelSuggestion
        ref="labelSuggestion"
        :chat-labels="activeLabels.map(l => l.title)"
        :contact-id="contactId"
        :disable-auto-fetch="true"
      />
    </div>
    <Spinner v-else />
  </div>
</template>

<style lang="scss" scoped>
.sidebar-labels-wrap {
  margin-bottom: 0;
}
.contact-conversation--list {
  width: 100%;

  .label-wrap {
    line-height: 1.5rem;
    position: relative;
  }

  // 侧边栏中标签建议组件宽度100%
  :deep(.label-suggestion) {
    width: 100%;
    padding: 0;
  }

  :deep(.suggestion-card) {
    width: 100%;
  }
}

.ai-suggest-btn {
  background: transparent;
  border: none;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.375rem;
  border-radius: 4px;
  height: 24px;
  transition: background 0.15s;
  color: var(--n-slate-12, #475569);
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.25rem;

  &:hover {
    background-color: var(--n-alpha-2, rgba(0, 0, 0, 0.03));
  }
}
</style>
